import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Connects to data-controller="catalog--item-picker"
// Opens a modal to search/filter/select catalog items for BOM part assignment
export default class extends Controller {
  static targets = [
    "modal",
    "searchInput",
    "kindFilter",
    "statusFilter",
    "tbody",
    "table",
    "loading",
    "empty",
    "display",
    "hiddenInput",
    "unitDisplay",
    "resultCount",
  ]
  static values = {
    url: String,
  }

  connect() {
    this._debounceTimer = null
    this._selectedIndex = -1
    this._results = []
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    // Reset any leftover body scroll-lock if the picker dialog is still open
    // when the controller unmounts (Turbo navigation, manual DOM removal).
    if (this.hasModalTarget) {
      const dialog =
        this.modalTarget.closest("dialog") ||
        this.modalTarget.querySelector("dialog")
      if (dialog?.open) {
        document.body.style.overflow = ""
      }
    }
  }

  // ── Modal lifecycle ──

  openModal() {
    const modal = this.modalTarget
    const dialog = modal.closest("dialog") || modal.querySelector("dialog")
    if (dialog && !dialog.open) {
      dialog.showModal()
      document.body.style.overflow = "hidden"
    }

    requestAnimationFrame(() => {
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.value = ""
        this.searchInputTarget.focus()
      }
      if (this.hasKindFilterTarget) this.#resetSelect(this.kindFilterTarget)
      if (this.hasStatusFilterTarget) this.#resetSelect(this.statusFilterTarget)
      this._fetchResults()
    })
  }

  // The kind/status filters are Tom Select-enhanced (Ui::SelectComponent), so a
  // plain `.value = ""` on the native <select> wouldn't update the visible
  // control. Use the Tom Select instance when present, silently (we re-fetch
  // explicitly right after), and fall back to the native select otherwise.
  #resetSelect(select) {
    if (select.tomselect) {
      select.tomselect.setValue("", true)
    } else {
      select.value = ""
    }
  }

  closeModal() {
    const modal = this.modalTarget
    const dialog = modal.closest("dialog") || modal.querySelector("dialog")
    if (dialog && dialog.open) {
      dialog.close()
      document.body.style.overflow = ""
    }
  }

  // ── Search & filter ──

  search() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this._fetchResults(), 300)
  }

  filter() {
    this._fetchResults()
  }

  // ── Keyboard navigation ──

  keydown(event) {
    const rows = this.tbodyTarget.querySelectorAll("tr[data-item-id]")
    if (!rows.length) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this._selectedIndex = Math.min(this._selectedIndex + 1, rows.length - 1)
        this._highlightRow(rows)
        break
      case "ArrowUp":
        event.preventDefault()
        this._selectedIndex = Math.max(this._selectedIndex - 1, 0)
        this._highlightRow(rows)
        break
      case "Enter":
        event.preventDefault()
        if (this._selectedIndex >= 0 && this._selectedIndex < rows.length) {
          this._selectFromRow(rows[this._selectedIndex])
        }
        break
      case "Escape":
        this.closeModal()
        break
    }
  }

  // ── Selection ──

  selectItem(event) {
    const row = event.currentTarget.closest("tr[data-item-id]")
    if (row) this._selectFromRow(row)
  }

  // ── Private ──

  async _fetchResults() {
    this._selectedIndex = -1
    this._showLoading(true)
    this._showEmpty(false)
    this._showTable(false)
    this._updateResultCount(null)

    const url = new URL(this.urlValue, window.location.origin)
    const q = this.hasSearchInputTarget
      ? this.searchInputTarget.value.trim()
      : ""
    if (q) url.searchParams.set("q", q)

    const kind = this.hasKindFilterTarget ? this.kindFilterTarget.value : ""
    if (kind) url.searchParams.set("kind", kind)

    const status = this.hasStatusFilterTarget
      ? this.statusFilterTarget.value
      : ""
    if (status) url.searchParams.set("status", status)

    try {
      const response = await get(url.toString(), { responseKind: "json" })

      if (!response.ok) throw new Error(`HTTP ${response.statusCode}`)

      const data = await response.json
      this._results = data.results || []
      this._renderResults(this._results)
      this._updateResultCount(data.total || this._results.length)
    } catch (error) {
      console.error("[item-picker] fetch error:", error)
      this._results = []
      this._renderResults([])
    } finally {
      this._showLoading(false)
    }
  }

  _renderResults(results) {
    const tbody = this.tbodyTarget
    tbody.innerHTML = ""

    if (results.length === 0) {
      this._showEmpty(true)
      this._showTable(false)
      return
    }

    this._showEmpty(false)
    this._showTable(true)

    results.forEach((item) => {
      const tr = document.createElement("tr")
      tr.dataset.itemId = item.id
      tr.dataset.itemCode = item.code
      tr.dataset.itemName = item.name || ""
      tr.dataset.itemUnit = item.unit || ""
      tr.className = "group hover:bg-primary/5 cursor-pointer transition-colors"
      tr.innerHTML = `
        <td class="px-4 py-2 text-sm font-mono font-medium text-foreground whitespace-nowrap">${this._escape(item.code)}</td>
        <td class="px-4 py-2 text-sm text-foreground">${this._escape(item.name || "\u2014")}</td>
        <td class="px-4 py-2 hidden sm:table-cell">${this._renderKindBadge(item.kind)}</td>
        <td class="px-4 py-2 hidden sm:table-cell">${this._renderStatusBadge(item.status)}</td>
        <td class="px-4 py-2 text-sm text-right font-mono text-muted-foreground hidden md:table-cell">${item.base_cost != null ? this._formatCost(item.base_cost) : "\u2014"}</td>
        <td class="px-4 py-2 text-right">
          <button type="button"
            class="inline-flex items-center justify-center rounded-md text-xs font-medium px-2.5 py-1 transition-colors text-primary/60 group-hover:bg-primary group-hover:text-primary-foreground group-hover:shadow-sm"
            data-action="click->catalog--item-picker#selectItem">Select</button>
        </td>
      `
      tr.addEventListener("dblclick", () => this._selectFromRow(tr))
      tbody.appendChild(tr)
    })
  }

  _selectFromRow(row) {
    const id = row.dataset.itemId
    const code = row.dataset.itemCode
    const name = row.dataset.itemName
    const unit = row.dataset.itemUnit || ""

    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = id
    }

    if (this.hasDisplayTarget) {
      this.displayTarget.textContent = `${code} \u2014 ${name}`
      this.displayTarget.classList.remove("text-muted-foreground")
      this.displayTarget.classList.add("text-foreground", "font-medium")
    }

    // CONEK-4618: reflect the selected item's unit in the read-only input.
    // The target is a disabled Ui::InputComponent, so we drive its `.value`
    // (blank falls back to the "\u2014" placeholder). Guarded so the other forms
    // sharing this controller (no unitDisplay target) are unaffected.
    if (this.hasUnitDisplayTarget) {
      this.unitDisplayTarget.value = unit || ""
    }

    this.closeModal()
  }

  _highlightRow(rows) {
    rows.forEach((row, idx) => {
      if (idx === this._selectedIndex) {
        row.classList.add("bg-primary/10")
        row.scrollIntoView({ block: "nearest" })
      } else {
        row.classList.remove("bg-primary/10")
      }
    })
  }

  _showLoading(visible) {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.toggle("hidden", !visible)
    }
  }

  _showEmpty(visible) {
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", !visible)
    }
  }

  _showTable(visible) {
    if (this.hasTableTarget) {
      this.tableTarget.classList.toggle("hidden", !visible)
    }
  }

  _updateResultCount(total) {
    if (!this.hasResultCountTarget) return
    if (total === null) {
      this.resultCountTarget.textContent = ""
    } else {
      const shown = this._results.length
      this.resultCountTarget.textContent =
        total > shown
          ? `Showing ${shown} of ${total} items`
          : `${total} item${total !== 1 ? "s" : ""}`
    }
  }

  _renderKindBadge(kind) {
    if (!kind) return '<span class="text-muted-foreground">\u2014</span>'
    const label = this._formatKind(kind)
    const colors = {
      product:
        "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
      part: "bg-slate-100 text-slate-700 dark:bg-slate-800/50 dark:text-slate-400",
      raw_material:
        "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
      consumable:
        "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
      service:
        "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
    }
    const colorClass = colors[kind] || "bg-muted text-muted-foreground"
    return `<span class="inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${colorClass}">${this._escape(label)}</span>`
  }

  _renderStatusBadge(status) {
    if (!status) return '<span class="text-muted-foreground">\u2014</span>'
    const label = this._formatStatus(status)
    const colors = {
      active:
        "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
      development:
        "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
      awaiting_launch:
        "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
      obsolescence:
        "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
      obsolete: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
      inactive:
        "bg-gray-100 text-gray-500 dark:bg-gray-800/50 dark:text-gray-500",
    }
    const colorClass = colors[status] || "bg-muted text-muted-foreground"
    return `<span class="inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${colorClass}">${this._escape(label)}</span>`
  }

  _formatKind(kind) {
    if (!kind) return "\u2014"
    return kind.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  }

  _formatStatus(status) {
    if (!status) return "\u2014"
    return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  }

  // Mirrors formatCurrency from @shared/lib/format-currency.ts (not importable in Stimulus JS)
  _formatCost(value) {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
    }).format(value)
  }

  _escape(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
